/**
 * Offline Request Queue
 * Queues failed API requests and retries them when connection is restored
 * Uses localStorage for persistence across page reloads
 */

const QUEUE_KEY = 'handshake_offline_queue';
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000; // 1 second

class OfflineQueue {
  constructor() {
    this.queue = this.loadQueue();
    this.isProcessing = false;
    this.listeners = new Set();
  }

  /**
   * Load queue from localStorage
   */
  loadQueue() {
    try {
      const queueData = localStorage.getItem(QUEUE_KEY);
      return queueData ? JSON.parse(queueData) : [];
    } catch (error) {
      console.error('Error loading offline queue from localStorage:', error);
      return [];
    }
  }

  /**
   * Save queue to localStorage
   */
  saveQueue() {
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(this.queue));
      this.notifyListeners();
    } catch (error) {
      console.error('Error saving offline queue to localStorage:', error);
    }
  }

  /**
   * Add a request to the queue
   * @param {Object} request - Request configuration
   * @param {string} request.method - HTTP method (GET, POST, PUT, DELETE)
   * @param {string} request.url - Request URL
   * @param {Object} request.data - Request payload
   * @param {Object} request.headers - Request headers
   * @param {Object} request.metadata - Additional metadata for UI display
   * @returns {string} - Queue item ID
   */
  enqueue(request) {
    const queueItem = {
      id: `queue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      ...request,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending' // pending, processing, failed, completed
    };

    this.queue.push(queueItem);
    this.saveQueue();

    console.log(`[OfflineQueue] Added request to queue:`, queueItem.id);
    return queueItem.id;
  }

  /**
   * Remove a request from the queue
   * @param {string} id - Queue item ID
   */
  dequeue(id) {
    const index = this.queue.findIndex(item => item.id === id);
    if (index !== -1) {
      this.queue.splice(index, 1);
      this.saveQueue();
      console.log(`[OfflineQueue] Removed request from queue:`, id);
    }
  }

  /**
   * Get all queued requests
   */
  getQueue() {
    return [...this.queue];
  }

  /**
   * Get queue size
   */
  size() {
    return this.queue.length;
  }

  /**
   * Clear the entire queue
   */
  clear() {
    this.queue = [];
    this.saveQueue();
    console.log('[OfflineQueue] Queue cleared');
  }

  /**
   * Process the queue
   * @param {Function} executor - Function that executes the request
   * @returns {Promise<Object>} - Results summary
   */
  async processQueue(executor) {
    if (this.isProcessing) {
      console.log('[OfflineQueue] Already processing queue');
      return { processed: 0, failed: 0, remaining: this.queue.length };
    }

    if (this.queue.length === 0) {
      console.log('[OfflineQueue] Queue is empty');
      return { processed: 0, failed: 0, remaining: 0 };
    }

    this.isProcessing = true;
    console.log(`[OfflineQueue] Processing ${this.queue.length} queued requests`);

    let processed = 0;
    let failed = 0;

    // Process items in order (FIFO)
    const itemsToProcess = [...this.queue];

    for (const item of itemsToProcess) {
      if (item.status === 'completed') {
        this.dequeue(item.id);
        continue;
      }

      try {
        item.status = 'processing';
        this.saveQueue();

        console.log(`[OfflineQueue] Processing request ${item.id} (retry ${item.retryCount}/${MAX_RETRIES})`);

        // Execute the request
        await executor(item);

        // Success - remove from queue
        this.dequeue(item.id);
        processed++;
        console.log(`[OfflineQueue] Request ${item.id} completed successfully`);

      } catch (error) {
        console.error(`[OfflineQueue] Request ${item.id} failed:`, error);

        item.retryCount++;
        item.lastError = error.message;
        item.status = 'pending';

        if (item.retryCount >= MAX_RETRIES) {
          // Max retries reached - mark as failed
          item.status = 'failed';
          failed++;
          console.error(`[OfflineQueue] Request ${item.id} failed after ${MAX_RETRIES} retries`);
        } else {
          // Calculate exponential backoff delay
          const delay = INITIAL_RETRY_DELAY * Math.pow(2, item.retryCount - 1);
          console.log(`[OfflineQueue] Will retry request ${item.id} in ${delay}ms`);

          // Wait before next retry
          await new Promise(resolve => setTimeout(resolve, delay));
        }

        this.saveQueue();
      }
    }

    this.isProcessing = false;
    console.log(`[OfflineQueue] Processing complete: ${processed} succeeded, ${failed} failed, ${this.queue.length} remaining`);

    return {
      processed,
      failed,
      remaining: this.queue.length
    };
  }

  /**
   * Retry all failed requests
   * @param {Function} executor - Function that executes the request
   */
  async retryFailed(executor) {
    // Reset retry count for failed items
    this.queue.forEach(item => {
      if (item.status === 'failed') {
        item.status = 'pending';
        item.retryCount = 0;
      }
    });
    this.saveQueue();

    return this.processQueue(executor);
  }

  /**
   * Subscribe to queue changes
   * @param {Function} listener - Callback function
   */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of queue changes
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener(this.getQueue());
      } catch (error) {
        console.error('[OfflineQueue] Error in listener:', error);
      }
    });
  }

  /**
   * Get failed requests
   */
  getFailedRequests() {
    return this.queue.filter(item => item.status === 'failed');
  }

  /**
   * Get pending requests
   */
  getPendingRequests() {
    return this.queue.filter(item => item.status === 'pending');
  }

  /**
   * Remove failed requests
   */
  clearFailed() {
    this.queue = this.queue.filter(item => item.status !== 'failed');
    this.saveQueue();
    console.log('[OfflineQueue] Failed requests cleared');
  }
}

// Create singleton instance
const offlineQueue = new OfflineQueue();

export default offlineQueue;
